// ─── Impostazioni → Maestri ──────────────────────────────────────────────────
//
// 📌 Un maestro non si cancella: si spegne. Cancellarlo lascerebbe delle lezioni
// senza chi le ha fatte, e quelle righe servono a pagarlo.

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  GIORNI,
  ORDINE_SETTIMANA,
  type BandaMaestro,
  type Campo as CampoDiGioco,
  type MaestroDiSegreteria,
} from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { perGiorni } from '../../lib/esiti.js';
import { Attesa, Campo, Guasto, Vuoto } from '../../components/pezzi.js';
import { CampoOra } from '../../components/CampoOra.js';
import { SoloLettura, usaModifica, usaRilettura } from './comune.js';

const CHIAVE = 'maestri';

/**
 * I gruppi di giorni che si spuntano in un colpo solo.
 *
 * ⚖️ Sono **tre e non di più**: sono i tre modi in cui un maestro lavora
 * davvero (tutta la settimana, i feriali, il fine settimana). Un elenco di
 * scorciatoie più lungo delle sette caselle che riempie sarebbe più lento da
 * leggere di quello che accorcia.
 * 🚨 I numeri sono quelli di Postgres — 0 = domenica (⇒ `GIORNI` in
 * `packages/shared/src/circolo.ts`, dove l'indice È il dato).
 */
const GRUPPI_DI_GIORNI: ReadonlyArray<{ nome: string; giorni: readonly number[] }> = [
  { nome: 'Tutti i giorni', giorni: ORDINE_SETTIMANA },
  { nome: 'Lun–ven', giorni: [1, 2, 3, 4, 5] },
  { nome: 'Sab e dom', giorni: [6, 0] },
];

export function Maestri(): React.JSX.Element {
  const modificabile = usaModifica('manage_settings');
  const elenco = useQuery({
    queryKey: [CHIAVE],
    queryFn: () => chiama<{ items: MaestroDiSegreteria[] }>('GET', '/settings/instructors'),
  });

  if (elenco.isLoading) return <Attesa cosa="i maestri" />;
  if (elenco.error) return <Guasto errore={elenco.error} />;
  const items = elenco.data?.items ?? [];

  return (
    <section>
      <header className="testata">
        <h2>Maestri</h2>
        <p className="tenue">
          {items.filter((m) => m.active).length} attivi su {items.length}.
        </p>
        <SoloLettura mostra={modificabile} />
      </header>

      {items.length === 0 ? (
        <Vuoto titolo="Nessun maestro" perché="Le lezioni hanno bisogno di qualcuno che le tenga." />
      ) : (
        <div className="tabella-scorre">
          <table>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Ordine</th>
                <th>Stato</th>
                <th>Accesso</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <Riga key={m.id} maestro={m} modificabile={modificabile} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modificabile && <Nuovo />}
    </section>
  );
}

function Riga({
  maestro,
  modificabile,
}: {
  maestro: MaestroDiSegreteria;
  modificabile: boolean;
}): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  /**
   * 🗣️ *«anche qui manca il modifica»* (17/09/2026). Il server accettava già
   * nome e ordine (`modificaMaestroSchema`): mancava solo il modo di
   * chiederglielo, e un maestro scritto con un refuso si poteva solo mettere a
   * riposo e riscrivere da capo — lasciando in elenco due righe per la stessa
   * persona.
   * ⚠️ Il pannello si apre in una RIGA SUA sotto, non dentro una cella: un
   *    modulo dentro un `<td>` allargherebbe la colonna e sfascerebbe la tabella.
   */
  const [aperto, setAperto] = useState(false);
  const cambia = useMutation({
    meta: {
      fatto: (_d, c) =>
        (c as { active: boolean }).active
          ? `${maestro.displayName} riattivato`
          : `${maestro.displayName} disattivato`,
    },
    mutationFn: (c: { active: boolean }) => chiama('PATCH', `/settings/instructors/${maestro.id}`, c),
    onSuccess: rileggi,
  });

  return (
    <>
      <tr className={maestro.active ? '' : 'spento'}>
        <td>{maestro.displayName}</td>
        <td>{maestro.sortOrder}</td>
        <td>{maestro.active ? 'attivo' : 'non più'}</td>
        <td>
          <StatoAccesso maestro={maestro} />
        </td>
        <td className="azioni">
          {modificabile && (
            <>
              <button
                type="button"
                className="piatto"
                aria-expanded={aperto}
                onClick={() => setAperto((v) => !v)}
              >
                Modifica
              </button>
              <button
                type="button"
                className="piatto"
                disabled={cambia.isPending}
                onClick={() => cambia.mutate({ active: !maestro.active })}
              >
                {maestro.active ? 'Metti a riposo' : 'Rimetti in servizio'}
              </button>
            </>
          )}
          <Guasto errore={cambia.error} />
        </td>
      </tr>
      {modificabile && aperto && (
        <tr className="riga-modifica">
          <td colSpan={5}>
            <div className="pannelli-riga">
              <Modifica maestro={maestro} chiudi={() => setAperto(false)} />
              <Accesso maestro={maestro} />
              <Perimetro maestro={maestro} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Cambiare un maestro già in elenco.
 *
 * ⭐ Si manda SOLO quello che è cambiato davvero: `modificaMaestroSchema` dice
 * «non toccarlo» per ciò che non arriva, quindi rispedire tutto riscriverebbe
 * anche i valori che nessuno ha guardato.
 *
 * ⚠️ Lo STATO (attivo / a riposo) non sta qui: ha già il suo bottone nella
 * riga. Due posti per lo stesso gesto sono due posti da tenere d'accordo.
 */
function Modifica({
  maestro,
  chiudi,
}: {
  maestro: MaestroDiSegreteria;
  chiudi: () => void;
}): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [nome, setNome] = useState(maestro.displayName);
  const [ordine, setOrdine] = useState(String(maestro.sortOrder));

  const cambiato: Record<string, unknown> = {};
  if (nome.trim() !== maestro.displayName) cambiato.displayName = nome.trim();
  if (Number(ordine) !== maestro.sortOrder) cambiato.sortOrder = Number(ordine);
  const niente = Object.keys(cambiato).length === 0 || nome.trim() === '';

  const salva = useMutation({
    meta: { fatto: `${maestro.displayName} salvato` },
    mutationFn: () => chiama('PATCH', `/settings/instructors/${maestro.id}`, cambiato),
    onSuccess: () => {
      rileggi();
      chiudi();
    },
  });

  return (
    <form
      className="pannello-fascia"
      onSubmit={(e) => {
        e.preventDefault();
        if (!niente) salva.mutate();
      }}
    >
      <div className="riga-campi">
        <Campo etichetta="Nome e cognome" required value={nome} onChange={(e) => setNome(e.target.value)} />
        <Campo
          etichetta="Ordine in elenco"
          type="number"
          min={0}
          max={999}
          required
          value={ordine}
          onChange={(e) => setOrdine(e.target.value)}
        />
      </div>
      <p className="tenue piccolo">
        Il nome è quello che si vede nella scheda della lezione. Per metterlo a riposo c’è il bottone nella
        riga.
      </p>
      <Guasto errore={salva.error} />
      <div className="azioni">
        <button type="submit" disabled={salva.isPending || niente}>
          {salva.isPending ? 'Un attimo…' : 'Salva'}
        </button>
        <button type="button" className="piatto" onClick={chiudi}>
          Annulla
        </button>
      </div>
    </form>
  );
}

/** Lo stato dell'accesso, in italiano: un'email nuda non dice se quella persona entra. */
function StatoAccesso({ maestro }: { maestro: MaestroDiSegreteria }): React.JSX.Element {
  const a = maestro.accesso;
  if (!a) return <span className="tenue">—</span>;
  /*
   * ⚠️ I tre stati sono DIVERSI e vogliono tre frasi diverse, o chi guarda
   * legge un'email e crede che quella persona stia entrando:
   *   · password non ancora scelta ⇒ l'accesso esiste ma **non entra**;
   *   · utente disattivato ⇒ la porta è chiusa da Utenti staff, non da qui;
   *   · tutto a posto ⇒ entra.
   */
  const nota = !a.active
    ? 'accesso disattivato'
    : !a.hasPassword
      ? 'deve ancora scegliere la password'
      : null;
  return (
    <span className={nota ? 'tenue' : undefined}>
      {a.email}
      {nota && <span className="piccolo"> — {nota}</span>}
    </span>
  );
}

/**
 * 🎾⭐ Aprire e chiudere l'accesso di un maestro.
 *
 * 🔒 Sta dietro `manage_users` e non dietro `manage_settings` come il resto di
 * questa schermata, perché **crea un accesso al gestionale**: il permesso si
 * chiede su ciò che il gesto fa, non su dove sta il bottone. Chi configura i
 * campi e i prezzi non apre per questo delle porte a delle persone.
 */
function Accesso({ maestro }: { maestro: MaestroDiSegreteria }): React.JSX.Element | null {
  const puoGestire = usaModifica('manage_users');
  const rileggi = usaRilettura(CHIAVE);
  const [email, setEmail] = useState('');

  const apri = useMutation({
    meta: { fatto: `accesso aperto per ${maestro.displayName}: la password la sceglie al primo accesso` },
    mutationFn: () => chiama('POST', `/settings/instructors/${maestro.id}/accesso`, { email: email.trim() }),
    onSuccess: () => {
      setEmail('');
      rileggi();
    },
  });
  const chiudi = useMutation({
    meta: { fatto: `accesso di ${maestro.displayName} chiuso` },
    mutationFn: () => chiama('DELETE', `/settings/instructors/${maestro.id}/accesso`),
    onSuccess: rileggi,
  });

  if (!puoGestire) return null;

  if (maestro.accesso) {
    return (
      <div className="riquadro">
        <h3>Accesso</h3>
        <p className="tenue piccolo">
          Entra con <strong>{maestro.accesso.email}</strong>.
          {!maestro.accesso.hasPassword &&
            ' Non ha ancora scelto la password: la sceglie da sé dalla schermata di accesso, con «È il mio primo accesso».'}
        </p>
        <Guasto errore={chiudi.error} />
        <button type="button" className="piatto" disabled={chiudi.isPending} onClick={() => chiudi.mutate()}>
          {chiudi.isPending ? 'Un attimo…' : 'Scollega questo accesso'}
        </button>
        {/* ⚖️ «Scollega», non «cancella», e la frase lo dice: si toglie il filo,
            la persona resta. Cancellare l'utente si fa da Utenti staff, ed è un
            altro gesto con altre conseguenze. */}
        <p className="tenue piccolo">
          Scollegare non cancella la persona: toglie solo il collegamento con questa scheda. L’accesso si
          disattiva da Impostazioni → Utenti staff.
        </p>
      </div>
    );
  }

  return (
    <form
      className="riquadro"
      onSubmit={(e) => {
        e.preventDefault();
        if (email.trim() !== '') apri.mutate();
      }}
    >
      <h3>Apri l’accesso</h3>
      <p className="tenue piccolo">
        Il maestro entrerà con questa email e vedrà <strong>solo le sue lezioni</strong>: del gestionale della
        segreteria non vede niente. La password se la sceglie da sé — nessuno gliene assegna una.
      </p>
      <Campo
        etichetta="Email"
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <Guasto errore={apri.error} />
      <button type="submit" disabled={apri.isPending || email.trim() === ''}>
        {apri.isPending ? 'Un attimo…' : 'Apri l’accesso'}
      </button>
    </form>
  );
}

function Nuovo(): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [nome, setNome] = useState('');

  const crea = useMutation({
    meta: { fatto: `maestro ${nome.trim()} aggiunto` },
    mutationFn: () => chiama('POST', '/settings/instructors', { displayName: nome, sortOrder: 0 }),
    onSuccess: () => {
      setNome('');
      rileggi();
    },
  });

  return (
    <form
      className="riquadro"
      onSubmit={(e) => {
        e.preventDefault();
        crea.mutate();
      }}
    >
      <h3>Aggiungi un maestro</h3>
      <p className="tenue piccolo">
        Il nome è quello che si vede nella scheda della lezione: si scrive come lo chiamano i soci.
      </p>
      <Campo etichetta="Nome e cognome" required value={nome} onChange={(e) => setNome(e.target.value)} />
      <Guasto errore={crea.error} />
      <button type="submit" disabled={crea.isPending}>
        {crea.isPending ? 'Un attimo…' : 'Aggiungi'}
      </button>
    </form>
  );
}

/**
 * 🎾⭐⭐ DOVE PUÒ PRENOTARE: il perimetro di questo maestro.
 *
 * 🗣️ *«maestro per maestro, sulla sua scheda»* (18/09/2026) ⇒ sta qui dentro,
 * nella riga di lui, e non in una schermata «Perimetri» a parte: un elenco
 * generale costringerebbe a scegliere il maestro due volte — una per aprirlo e
 * una per filtrarlo.
 *
 * ⛔ **NON è il componente `Foglio`**, che è il modo in cui la segreteria
 *    disegna le fasce del circolo. Due ragioni, e la prima basterebbe:
 *    `features/foglio/Foglio.tsx` trascina con i **soli eventi del mouse** —
 *    col dito non funziona; e comunque un perimetro sono poche righe, che in
 *    elenco si leggono meglio che in una griglia 7×N da interpretare.
 *
 * 🔒 `manage_settings`, come le fasce del circolo: qui si dice *a che ora può
 *    prendere un campo*, non *chi entra* — quello è il pannello accanto, ed è
 *    l'unico di questa schermata a stare dietro `manage_users`.
 */
function Perimetro({ maestro }: { maestro: MaestroDiSegreteria }): React.JSX.Element | null {
  const puoGestire = usaModifica('manage_settings');
  const rileggi = usaRilettura(CHIAVE);
  const base = `/settings/instructors/${maestro.id}/perimetro`;

  const bande = useQuery({
    // ⚙️ La chiave comincia per `maestri`, quindi `usaRilettura(CHIAVE)`
    // rilegge anche questa: una sola invalidazione per tutta la scheda.
    queryKey: [CHIAVE, maestro.id, 'perimetro'],
    queryFn: () => chiama<{ items: BandaMaestro[] }>('GET', base),
  });
  const campi = useQuery({
    queryKey: ['campi'],
    queryFn: () => chiama<{ items: CampoDiGioco[] }>('GET', '/settings/courts'),
  });

  const togli = useMutation({
    meta: { fatto: 'orario del maestro tolto' },
    mutationFn: (id: string) => chiama('DELETE', `${base}/${id}`),
    onSuccess: rileggi,
  });

  if (!puoGestire) return null;

  const items = bande.data?.items ?? [];
  const nomeCampo = (id: string | null): string =>
    id === null ? 'Tutti i campi' : (campi.data?.items.find((c) => c.id === id)?.name ?? 'Campo tolto');

  return (
    <div className="riquadro">
      <h3>Dove può prenotare</h3>
      <p className="tenue piccolo">
        Il maestro può mettersi <strong>solo</strong> in queste ore, e solo su questi campi. Senza nessuna
        banda non può prenotare niente.
      </p>
      {/*
        ⚠️ Questa frase non è un aiuto: è l'unica cosa che dice a chi configura
        che QUESTA schermata e quella delle fasce si comportano all'opposto. Là
        una riga dedicata a un campo sostituisce le generali; qui si somma. Chi
        arriva dalla schermata delle fasce si porta dietro l'aspettativa
        sbagliata, e nessun errore gliela smentirebbe.
      */}
      <p className="tenue piccolo">
        Le bande <strong>si sommano</strong>: aggiungerne una non ne toglie nessun’altra. Due bande attaccate
        — 9–11 e 11–13 — valgono come una sola dalle 9 alle 13.
      </p>

      {bande.isLoading ? (
        <Attesa cosa="il perimetro" />
      ) : items.length === 0 ? (
        <p className="tenue piccolo">Nessuna banda: oggi non può prenotare da sé.</p>
      ) : (
        <ul className="elenco-bande">
          {ORDINE_SETTIMANA.filter((g) => items.some((b) => b.weekday === g)).map((g) => (
            <li key={g}>
              <strong>{GIORNI[g]}</strong>
              <ul>
                {items
                  .filter((b) => b.weekday === g)
                  .map((b) => (
                    <li key={b.id}>
                      <span>
                        {nomeCampo(b.courtId)} · {b.startsAtTime}–{b.endsAtTime}
                      </span>
                      <button
                        type="button"
                        className="piatto"
                        disabled={togli.isPending}
                        onClick={() => togli.mutate(b.id)}
                      >
                        Togli
                      </button>
                    </li>
                  ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
      <Guasto errore={bande.error} />
      <Guasto errore={togli.error} />

      <AggiungiBanda base={base} campi={campi.data?.items ?? []} />
    </div>
  );
}

/**
 * ⭐⭐ UNA BANDA SU PIÙ GIORNI IN UN COLPO SOLO — 18/09/2026.
 *
 * 🗣️ *«dammi la possibilità di scegliere più velocemente se voglio attivare
 *    più di un giorno della settimana contemporaneamente»*. Prima il giorno era
 *    una tendina: aprire lunedì-mercoledì-venerdì 15:00–18:00 voleva dire
 *    ribattere le stesse due ore **tre volte**.
 *
 * ⚖️ **Le caselle sono le stesse di `DuplicaTante` in `Fasce.tsx`**
 *    (`.giorni-scelta`), e lo sono apposta: la segreteria ha già imparato lì
 *    che spuntare più giorni è il modo di dire «anche». Un secondo modo di
 *    scegliere i giorni nella stessa sezione sarebbe una cosa in più da
 *    imparare per niente.
 *
 * ⚠️ **È una scrittura per giorno, non una chiamata sola**, perché una banda È
 *    una riga per giorno (⇒ `instructor_slots`). ⇒ Un giorno può essere
 *    rifiutato — la banda identica c'è già — mentre gli altri passano, e
 *    l'esito si dice **giorno per giorno**.
 * 📌 *Un «non ha funzionato» su cinque giorni di cui quattro scritti manderebbe
 *    la segreteria a riscrivere quello che c'è già.*
 */
function AggiungiBanda({ base, campi }: { base: string; campi: CampoDiGioco[] }): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const TUTTI = 'tutti';
  const [giorni, setGiorni] = useState<number[]>([1]);
  const [campo, setCampo] = useState(TUTTI);
  const [inizio, setInizio] = useState('');
  const [fine, setFine] = useState('');

  const pronto = giorni.length > 0 && inizio !== '' && fine !== '' && fine > inizio;
  // Nell'ordine in cui si LEGGONO (lunedì…domenica), non in quello in cui sono
  // stati spuntati: l'esito deve scorrere come le caselle sopra.
  const scelti = ORDINE_SETTIMANA.filter((g) => giorni.includes(g));

  const aggiungi = useMutation({
    meta: { fatto: (d) => perGiorni(d as EsitoBanda[], 'orario del maestro aggiunto') },
    mutationFn: async (): Promise<EsitoBanda[]> => {
      const esiti: EsitoBanda[] = [];
      // In fila e non in parallelo: così l'errore che torna resta attaccato al
      // suo giorno senza doverlo ricostruire.
      for (const g of scelti) {
        try {
          await chiama('POST', base, {
            courtId: campo === TUTTI ? null : campo,
            weekday: g,
            startsAtTime: inizio,
            endsAtTime: fine,
          });
          esiti.push({ giorno: g, errore: null });
        } catch (e) {
          esiti.push({ giorno: g, errore: (e as Error).message });
        }
      }
      return esiti;
    },
    onSuccess: (esiti) => {
      rileggi();
      // ⚖️ Si svuotano le ORE, non i giorni: chi apre un perimetro scrive quasi
      // sempre più bande sugli stessi giorni (mattina e pomeriggio), e
      // rispuntarli ogni volta era metà del lavoro che si voleva togliere.
      // Le ore restano scritte se qualcosa è stato rifiutato, o si perderebbe
      // proprio ciò che serve per riprovare.
      if (esiti.every((e) => e.errore === null)) {
        setInizio('');
        setFine('');
      }
    },
  });

  const esiti = aggiungi.data ?? [];
  const scritte = esiti.filter((e) => e.errore === null).length;
  const rifiutati = esiti.filter((e) => e.errore !== null);
  // I rifiuti raccolti per motivo, nell'ordine in cui sono arrivati: quasi
  // sempre è **uno solo** per tutti i giorni, e ripeterlo N volte lo renderebbe
  // illeggibile proprio quando i giorni sono tanti.
  const perMotivo = [
    ...new Map<string, number[]>(
      rifiutati.map((e) => [e.errore!, rifiutati.filter((x) => x.errore === e.errore).map((x) => x.giorno)]),
    ),
  ];

  return (
    <form
      className="pannello-fascia"
      onSubmit={(e) => {
        e.preventDefault();
        if (pronto) aggiungi.mutate();
      }}
    >
      <fieldset className="scelta-giorni">
        <legend>Giorni</legend>
        <div className="giorni-scelta">
          {ORDINE_SETTIMANA.map((i) => (
            <label key={i} className="campo orizzontale">
              <input
                type="checkbox"
                disabled={aggiungi.isPending}
                checked={giorni.includes(i)}
                onChange={(e) => setGiorni((p) => (e.target.checked ? [...p, i] : p.filter((g) => g !== i)))}
              />
              <span>{GIORNI[i]}</span>
            </label>
          ))}
        </div>
        {/* ⭐ Le scorciatoie non sono un'alternativa alle caselle: le riempiono.
            Dopo «Lun–ven» si vede quali sette caselle sono spuntate e se ne può
            togliere una — un gruppo che prenotasse da sé non lo mostrerebbe. */}
        <div className="scorciatoie">
          {GRUPPI_DI_GIORNI.map((g) => (
            <button
              key={g.nome}
              type="button"
              className="piatto"
              disabled={aggiungi.isPending}
              onClick={() => setGiorni([...g.giorni])}
            >
              {g.nome}
            </button>
          ))}
          <button
            type="button"
            className="piatto"
            disabled={aggiungi.isPending || giorni.length === 0}
            onClick={() => setGiorni([])}
          >
            Nessuno
          </button>
        </div>
      </fieldset>

      <div className="riga-campi">
        <label className="campo">
          <span>Campo</span>
          {/* ⭐ I campi spenti restano in elenco con lo stato scritto accanto:
              toglierli farebbe sparire un campo senza dire che esiste — difetto
              già pagato una volta in `Fasce.tsx`. */}
          <select value={campo} onChange={(e) => setCampo(e.target.value)}>
            <option value={TUTTI}>Tutti i campi</option>
            {campi.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.active ? '' : ' (spento)'}
              </option>
            ))}
          </select>
        </label>
        <CampoOra etichetta="Dalle" value={inizio} onChange={setInizio} />
        <CampoOra etichetta="Alle" value={fine} onChange={setFine} />
      </div>

      {esiti.length > 0 && (
        <p
          className={rifiutati.length > 0 ? 'guasto' : 'tenue piccolo'}
          role={rifiutati.length > 0 ? 'alert' : undefined}
        >
          {scritte > 0 && (
            <>
              {scritte === 1 ? 'Una banda scritta' : `${scritte} bande scritte`}
              {rifiutati.length > 0 ? '. ' : '.'}
            </>
          )}
          {/* ⚖️ I giorni si raggruppano per MOTIVO, non si elencano uno per uno.
              📏 Guardato sullo schermo: cinque giorni su un perimetro già
              scritto davano quattro volte la stessa frase di ventidue parole —
              quattro righe rosse per un fatto solo. Il motivo si legge una
              volta; quello che cambia sono i giorni. */}
          {perMotivo.map(([motivo, giorniDelMotivo]) => (
            <span key={motivo}>
              {giorniDelMotivo.map((g) => GIORNI[g]).join(', ')}: {motivo}{' '}
            </span>
          ))}
        </p>
      )}
      {/* Il guasto che non appartiene a nessun giorno: la rete caduta a metà giro. */}
      <Guasto errore={aggiungi.error} />

      <button type="submit" disabled={aggiungi.isPending || !pronto}>
        {aggiungi.isPending
          ? 'Un attimo…'
          : scelti.length > 1
            ? `Aggiungi la banda su ${scelti.length} giorni`
            : 'Aggiungi la banda'}
      </button>
    </form>
  );
}

/** Com'è andata su UN giorno: la banda è una riga per giorno, e così l'esito. */
interface EsitoBanda {
  giorno: number;
  errore: string | null;
}
